import React, {
  useCallback,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'
import { ReactComponent as ArrowDownCenterIcon } from '@assets/images/Tooltip/arrow-down-center.svg'
import { ReactComponent as ArrowLeftIcon } from '@assets/images/Tooltip/arrow-left.svg'
import { ReactComponent as ArrowRightIcon } from '@assets/images/Tooltip/arrow-right.svg'
import type { TooltipPlacement, TooltipProps } from './Tooltip.types'
import { TOOLTIP_DEFAULT_CONTENT } from './Tooltip.types'
import './Tooltip.css'

const TOOLTIP_GAP = 4

const TooltipArrow: React.FC<{ placement: TooltipPlacement }> = ({
  placement,
}) => {
  if (placement === 'left') {
    return <ArrowLeftIcon className="ds-tooltip__arrow-graphic" />
  }

  if (placement === 'right') {
    return <ArrowRightIcon className="ds-tooltip__arrow-graphic" />
  }

  return <ArrowDownCenterIcon className="ds-tooltip__arrow-graphic" />
}

const getPortalStyle = (
  placement: TooltipPlacement,
  rect: DOMRect,
): React.CSSProperties => {
  switch (placement) {
    case 'right':
      return {
        top: rect.top + rect.height / 2,
        left: rect.right + TOOLTIP_GAP,
        transform: 'translateY(-50%)',
      }
    case 'left':
      return {
        top: rect.top + rect.height / 2,
        left: rect.left - TOOLTIP_GAP,
        transform: 'translate(-100%, -50%)',
      }
    case 'bottom':
    case 'bottom-left':
    case 'bottom-right':
      return {
        top: rect.bottom + TOOLTIP_GAP,
        left: rect.left + rect.width / 2,
        transform: 'translateX(-50%)',
      }
    case 'top':
    case 'top-left':
    case 'top-right':
    default:
      return {
        top: rect.top - TOOLTIP_GAP,
        left: rect.left + rect.width / 2,
        transform: 'translate(-50%, -100%)',
      }
  }
}

const Tooltip: React.FC<TooltipProps> = ({
  content = TOOLTIP_DEFAULT_CONTENT,
  placement = 'top',
  children,
  className = '',
  darkMode = false,
  open,
  portal = false,
}) => {
  const tooltipId = useId()
  const triggerRef = useRef<HTMLSpanElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [portalStyle, setPortalStyle] = useState<React.CSSProperties | null>(
    null,
  )
  const isStatic = children == null
  const isOpen = open ?? (isStatic || isHovered)
  const arrowFirst = placement.startsWith('bottom') || placement === 'right'

  const bubbleClassName = useMemo(
    () =>
      [
        'ds-tooltip',
        `ds-tooltip--${placement}`,
        darkMode ? 'ds-tooltip--dark' : '',
        className,
      ]
        .filter(Boolean)
        .join(' '),
    [className, darkMode, placement],
  )

  const updatePortalPosition = useCallback((): void => {
    if (!portal || !triggerRef.current) {
      return
    }

    setPortalStyle(
      getPortalStyle(placement, triggerRef.current.getBoundingClientRect()),
    )
  }, [placement, portal])

  useLayoutEffect(() => {
    if (!portal || !isOpen) {
      setPortalStyle(null)
      return undefined
    }

    updatePortalPosition()

    const handleReposition = (): void => updatePortalPosition()
    window.addEventListener('scroll', handleReposition, true)
    window.addEventListener('resize', handleReposition)

    return () => {
      window.removeEventListener('scroll', handleReposition, true)
      window.removeEventListener('resize', handleReposition)
    }
  }, [isOpen, portal, updatePortalPosition])

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false)
  }, [])

  const handleFocus = useCallback(() => {
    setIsHovered(true)
  }, [])

  const handleBlur = useCallback(() => {
    setIsHovered(false)
  }, [])

  const arrow = (
    <span className="ds-tooltip__arrow" aria-hidden="true">
      <TooltipArrow placement={placement} />
    </span>
  )

  const body = <div className="ds-tooltip__body">{content}</div>

  const bubble = (
    <div
      id={tooltipId}
      className={bubbleClassName}
      role="tooltip"
      aria-hidden={!isOpen}
    >
      {arrowFirst ? arrow : null}
      {body}
      {arrowFirst ? null : arrow}
    </div>
  )

  if (isStatic) {
    return bubble
  }

  const popup = isOpen ? (
    <span
      className={`ds-tooltip-anchor__popup ds-tooltip-anchor__popup--${placement}${
        portal ? ' ds-tooltip-anchor__popup--portal' : ''
      }`}
      style={portal ? (portalStyle ?? undefined) : undefined}
    >
      {bubble}
    </span>
  ) : null

  return (
    <span
      className="ds-tooltip-anchor"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <span
        ref={triggerRef}
        className="ds-tooltip-anchor__trigger"
        aria-describedby={isOpen ? tooltipId : undefined}
      >
        {children}
      </span>
      {portal && popup && typeof document !== 'undefined'
        ? createPortal(popup, document.body)
        : popup}
    </span>
  )
}

export default Tooltip
